import React, { useState } from 'react';
import {
  X,
  Mail,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Calendar,
  Globe,
  Ban,
  CheckCircle,
  AlertTriangle,
  Send,
} from 'lucide-react';
import Swal from 'sweetalert2';
import { excludeSubscriber, includeSubscriber } from '../../../services/newsNewsletterApi';

export default function SubscriberDetailModal({
  isOpen,
  onClose,
  subscriber,
  onSubscriberUpdated,
}) {
  const [isActionLoading, setIsActionLoading] = useState(false);

  if (!isOpen || !subscriber) return null;

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleExclude = async () => {
    const { value: reason } = await Swal.fire({
      title: 'Exclure cet abonné (Blacklist) ?',
      text: 'Indiquez le motif de mise sur liste noire :',
      input: 'textarea',
      inputPlaceholder: 'Ex: Demande manuelle par courrier, plainte client, adresse suspecte...',
      inputValidator: (val) => {
        if (!val || val.trim().length < 5) {
          return 'Le motif d’exclusion doit comporter au moins 5 caractères.';
        }
      },
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmer l’exclusion',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#E53E3E',
    });

    if (reason) {
      setIsActionLoading(true);
      try {
        await excludeSubscriber(subscriber._id, reason.trim());
        Swal.fire('Exclu', 'L’abonné a été placé en liste noire.', 'success');
        onSubscriberUpdated?.();
        onClose();
      } catch (err) {
        Swal.fire('Erreur', err.message || 'Impossible d’exclure l’abonné', 'error');
      } finally {
        setIsActionLoading(false);
      }
    }
  };

  const handleInclude = async () => {
    const confirm = await Swal.fire({
      title: 'Réintégrer cet abonné ?',
      text: 'L’abonné sera réactivé dans les listes d’envoi si son consentement est toujours valide.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, réintégrer',
      cancelButtonText: 'Annuler',
    });

    if (confirm.isConfirmed) {
      setIsActionLoading(true);
      try {
        await includeSubscriber(subscriber._id);
        Swal.fire('Réintégré', 'L’abonné a été réintégré avec succès.', 'success');
        onSubscriberUpdated?.();
        onClose();
      } catch (err) {
        Swal.fire('Erreur', err.message || 'Impossible de réintégrer l’abonné', 'error');
      } finally {
        setIsActionLoading(false);
      }
    }
  };

  const isConsentGranted = subscriber.consent?.granted || subscriber.consentGiven;
  const isUnsubscribed = subscriber.status === 'UNSUBSCRIBED';

  return (
    <div className="an-modal-backdrop animate-fade-in" onClick={onClose}>
      <div className="an-modal-card an-modal-medium" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="an-modal-header">
          <div className="an-modal-title">
            <Mail size={22} className="text-primary mr-2" />
            <span>Fiche Détaillée Abonné</span>
          </div>
          <button className="an-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="an-modal-body">
          {/* Identity Box */}
          <div className="an-subscriber-id-box">
            <div className="an-sub-email">{subscriber.email}</div>
            <div className="an-sub-name">{subscriber.fullName || 'Nom non renseigné'}</div>
            <div className="an-sub-status-bar mt-2">
              <span className={`an-badge an-badge-sub-${subscriber.status?.toLowerCase()}`}>
                {subscriber.status}
              </span>
              {subscriber.isExcluded ? (
                <span className="an-badge an-badge-danger">
                  <Ban size={12} className="mr-1" />
                  Exclu administrativement (Blacklisté)
                </span>
              ) : (
                <span className="an-badge an-badge-success">
                  <CheckCircle size={12} className="mr-1" />
                  Liste active
                </span>
              )}
              {subscriber.isConfirmed ? (
                <span className="an-badge an-badge-info">Email Confirmé</span>
              ) : (
                <span className="an-badge an-badge-warning">Email Non Confirmé</span>
              )}
            </div>
          </div>

          {/* Legal Consent Proof Box (CRITICAL RGPD / INPDP) */}
          <div className="an-meta-card border-primary mt-3">
            <h4 className="text-primary d-flex align-items-center">
              <ShieldCheck size={18} className="mr-2" />
              Preuve de Consentement Valide (Conformité INPDP & RGPD)
            </h4>
            <div className="an-meta-row">
              <span className="an-meta-label">Consentement actif :</span>
              <span className="an-meta-value font-bold">
                {isConsentGranted ? (
                  <span className="text-success">OUI (Accord explicite donné)</span>
                ) : (
                  <span className="text-danger">NON (Révoqué ou absent)</span>
                )}
              </span>
            </div>
            <div className="an-meta-row">
              <span className="an-meta-label">Méthode de collecte :</span>
              <span className="an-meta-value">
                {subscriber.consent?.collectionMethod || 'SITE_FORM'}
              </span>
            </div>
            <div className="an-meta-row">
              <span className="an-meta-label">Date et heure du consentement :</span>
              <span className="an-meta-value">
                {formatDate(subscriber.consent?.grantedAt || subscriber.createdAt)}
              </span>
            </div>
            <div className="an-meta-row">
              <span className="an-meta-label">Adresse IP de preuve :</span>
              <span className="an-meta-value font-mono">
                {subscriber.consent?.proofIpAddress || 'Non enregistrée'}
              </span>
            </div>
          </div>

          {/* Warning on Unsubscribed Users */}
          {isUnsubscribed && (
            <div className="an-alert-box warning mt-3">
              <AlertTriangle size={18} className="mr-2" />
              <div>
                <strong>Abonné désabonné :</strong> Conformément à la législation sur la protection des données personnelles, un administrateur ne peut pas réabonner manuellement cet utilisateur. Seul l'utilisateur peut renouveler son inscription via un nouveau consentement.
              </div>
            </div>
          )}

          {/* Exclusion Notice */}
          {subscriber.isExcluded && subscriber.exclusionDetails?.reason && (
            <div className="an-alert-box danger mt-3">
              <Ban size={18} className="mr-2" />
              <div>
                <strong>Motif de la mise sur liste noire :</strong>
                <p className="mb-0 mt-1">"{subscriber.exclusionDetails.reason}"</p>
                <small className="text-muted">
                  Exclu le {formatDate(subscriber.exclusionDetails.excludedAt)}
                </small>
              </div>
            </div>
          )}

          {/* Delivery & Activity Stats */}
          <div className="an-meta-card mt-3">
            <h4>Statistiques d'Envoi</h4>
            <div className="an-meta-row">
              <span className="an-meta-label">Total emails expédiés :</span>
              <span className="an-meta-value">{subscriber.metrics?.emailsSentCount || 0}</span>
            </div>
            <div className="an-meta-row">
              <span className="an-meta-label">Dernier envoi reçu :</span>
              <span className="an-meta-value">
                {formatDate(subscriber.metrics?.lastEmailSentAt)}
              </span>
            </div>
            <div className="an-meta-row">
              <span className="an-meta-label">Langue préférée :</span>
              <span className="an-meta-value font-bold text-uppercase">
                {subscriber.preferredLocale || 'fr'}
              </span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="an-modal-footer">
          <button type="button" className="an-btn-secondary" onClick={onClose}>
            Fermer
          </button>

          {!subscriber.isExcluded && !isUnsubscribed && (
            <button
              type="button"
              className="an-btn-danger"
              onClick={handleExclude}
              disabled={isActionLoading}
            >
              <Ban size={16} />
              <span>Exclure de la liste</span>
            </button>
          )}

          {subscriber.isExcluded && !isUnsubscribed && (
            <button
              type="button"
              className="an-btn-success"
              onClick={handleInclude}
              disabled={isActionLoading}
            >
              <CheckCircle size={16} />
              <span>Réintégrer l'abonné</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

